import { useEffect, useState } from "react";
import { useRoute } from "wouter";
import { supabase } from "@/lib/supabase";
import LoadingSpinner from "@/components/LoadingSpinner";
import SEO from '@/components/SEO';

export default function ViewCatalog() {
  const [, params] = useRoute("/ver-catalogo/:slug");
  const slug = params?.slug;
  const [loading, setLoading] = useState(true);
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Obtener parámetros de la URL directamente
  const searchParams = typeof window !== 'undefined'
    ? new URLSearchParams(window.location.search)
    : new URLSearchParams();
  const fileParam = searchParams.get('file');

  useEffect(() => {
    let objectUrl: string | null = null;

    async function loadAndDisplayPdf() {
      setLoading(true);
      setError(null);
      try {
        if (!fileParam) {
          throw new Error("No se proporcionó ningún archivo PDF");
        }

        // 1. Obtener URL pública de Supabase
        const { data: urlData, error: urlError } = supabase.storage
          .from('pdfs')
          .getPublicUrl(fileParam);

        if (urlError || !urlData?.publicUrl) {
          throw new Error(urlError?.message || "No se pudo obtener la URL del PDF");
        }

        // 2. Descargar el contenido del PDF
        const response = await fetch(urlData.publicUrl);
        if (!response.ok) {
          throw new Error(`Error al descargar el PDF: ${response.statusText}`);
        }
        const pdfBlob = await response.blob();

        // 3. Crear URL de tipo Blob
        objectUrl = URL.createObjectURL(pdfBlob);
        setPdfBlobUrl(objectUrl);

      } catch (err: any) {
        console.error("Error al cargar y mostrar el PDF:", err);
        setError(err.message || "Ocurrió un error inesperado");
      } finally {
        setLoading(false);
      }
    }

    loadAndDisplayPdf();

    // Limpieza: Revocar la URL del objeto cuando el componente se desmonte
    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [fileParam]);

  // Si está cargando, mostrar spinner
  if (loading) {
    return (
      <div className="fixed inset-0 bg-[#0A0A0B] flex items-center justify-center z-50">
        <LoadingSpinner />
      </div>
    );
  }

  // Si hubo un error
  if (error) {
    return (
      <div className="min-h-screen bg-[#0A0A0B] flex flex-col items-center justify-center p-4">
        <SEO title="Error al cargar Catálogo" description={error} />
        <h1 className="text-2xl font-bold text-white mb-4">Error al cargar el catálogo</h1>
        <p className="text-zinc-400 mb-6 text-center">
          {error}
        </p>
        <a href="/catalogos" className="px-4 py-2 bg-[#FCAA0D] text-white rounded-lg font-medium">
          Volver a catálogos
        </a>
      </div>
    );
  }

  // Si se generó la URL del Blob, mostrar el iframe
  if (pdfBlobUrl) {
    // Generar título desde el slug para SEO
    const pageTitle = slug
      ?.split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ') || "Catálogo";

    return (
      <div className="fixed inset-0 w-full h-full bg-black">
        <SEO
          title={`${pageTitle} | Innotec Food Equipment`}
          description={`Visualiza el catálogo ${pageTitle}.`}
          canonicalUrl={`/ver-catalogo/${slug}`}
        />
        <iframe
          src={pdfBlobUrl}
          title={pageTitle}
          className="w-full h-full border-none"
          frameBorder="0"
        />
      </div>
    );
  }

  // Fallback por si algo inesperado ocurre
  return null;
}
